import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiError } from "@/lib/api";
import { WALLET_KEY } from "@/hooks/queries/useWallet";

// Agency client accounts (/api/agency). Every mutation that moves money also
// invalidates the wallet tree (the agency's balance changes).

export const AGENCY_KEY = ["agency"] as const;

export type ClientStatus = "active" | "suspended";

export interface AgencySummary {
  plan: { name: string; displayName: string } | null;
  maxClients: number;
  clientCount: number;
  earnings: { last30Days: number; allTime: number };
  clientSpendLast30Days: number;
  /** Provider cost reimbursed for requests on your keys (credited, not profit). */
  reimbursedLast30Days: number;
}

export interface AgencyClient {
  id: string;
  clientUserId: number;
  name: string;
  email: string | null;
  markupPercent: number;
  status: ClientStatus;
  balance: number;
  spendLast30Days: number;
  earningsLast30Days: number;
  requestsLast30Days: number;
  createdAt: string;
}

export interface ClientApiKey {
  id: number;
  name: string;
  keyPreview: string | null;
  status: string;
  createdAt: string;
  lastUsed: string | null;
}

export interface ClientUsage {
  days: number;
  totals: { requests: number; charged: number; agencyEarning: number };
  daily: { date: string; requests: number; charged: number; agencyEarning: number }[];
  byModel: { provider: string; model: string; requests: number; charged: number; agencyEarning: number }[];
}

export interface NewClientInput {
  name: string;
  email: string;
  password?: string;
  markupPercent: number;
  initialCredit?: number;
}

const toNum = (v: unknown, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
};

const parseClient = (c: Record<string, unknown>): AgencyClient => ({
  id: String(c.id),
  clientUserId: toNum(c.clientUserId),
  name: String(c.name ?? ""),
  email: typeof c.email === "string" ? c.email : null,
  markupPercent: toNum(c.markupPercent),
  status: c.status === "suspended" ? "suspended" : "active",
  balance: toNum(c.balance),
  spendLast30Days: toNum(c.spendLast30Days),
  earningsLast30Days: toNum(c.earningsLast30Days),
  requestsLast30Days: toNum(c.requestsLast30Days),
  createdAt: String(c.createdAt ?? ""),
});

const isForbidden = (e: unknown) => (e as ApiError | null)?.status === 403;

/**
 * The agency summary, or null when the account isn't an agency (403). Also
 * drives the visibility of the Clients nav entry.
 */
export function useAgencySummary(enabled = true) {
  return useQuery({
    queryKey: [...AGENCY_KEY, "summary"],
    enabled,
    staleTime: 60 * 1000,
    retry: (count, e) => !isForbidden(e) && count < 2,
    queryFn: async (): Promise<AgencySummary | null> => {
      try {
        const r = await api.get("/agency/summary");
        return {
          plan: r.plan ?? null,
          maxClients: toNum(r.maxClients),
          clientCount: toNum(r.clientCount),
          earnings: { last30Days: toNum(r.earnings?.last30Days), allTime: toNum(r.earnings?.allTime) },
          clientSpendLast30Days: toNum(r.clientSpendLast30Days),
          reimbursedLast30Days: toNum(r.reimbursedLast30Days),
        };
      } catch (e) {
        if (isForbidden(e)) return null;
        throw e;
      }
    },
  });
}

/** True once the summary loaded and the account is an agency. */
export function useIsAgency(enabled = true) {
  const q = useAgencySummary(enabled);
  return !!q.data;
}

export function useAgencyClients(enabled = true) {
  return useQuery({
    queryKey: [...AGENCY_KEY, "clients"],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AgencyClient[]> => {
      const r = await api.get("/agency/clients");
      return (Array.isArray(r.data) ? r.data : []).map(parseClient);
    },
  });
}

function useInvalidateAgency() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: AGENCY_KEY });
    qc.invalidateQueries({ queryKey: WALLET_KEY });
  };
}

export function useCreateClient() {
  const invalidate = useInvalidateAgency();
  return useMutation({
    mutationFn: async (
      input: NewClientInput
    ): Promise<{ client: AgencyClient; apiKey: string; initialCreditError: string | null }> => {
      const r = await api.post("/agency/clients", input);
      return {
        client: parseClient(r.client ?? {}),
        apiKey: String(r.apiKey?.key ?? ""),
        initialCreditError: typeof r.initialCreditError === "string" ? r.initialCreditError : null,
      };
    },
    onSuccess: invalidate,
  });
}

export function useUpdateClient() {
  const invalidate = useInvalidateAgency();
  return useMutation({
    mutationFn: async ({
      id,
      ...changes
    }: { id: string; name?: string; markupPercent?: number; status?: ClientStatus }): Promise<AgencyClient> =>
      parseClient(await api.patch(`/agency/clients/${encodeURIComponent(id)}`, changes)),
    onSuccess: invalidate,
  });
}

export function useClientCredit() {
  const invalidate = useInvalidateAgency();
  return useMutation({
    mutationFn: async ({
      id,
      amount,
      direction,
    }: {
      id: string;
      amount: number;
      direction: "to_client" | "to_agency";
    }): Promise<{ agencyBalance: number; clientBalance: number }> => {
      const r = await api.post(`/agency/clients/${encodeURIComponent(id)}/credit`, { amount, direction });
      return { agencyBalance: toNum(r.agencyBalance), clientBalance: toNum(r.clientBalance) };
    },
    onSuccess: invalidate,
  });
}

export function useClientApiKeys(id: string | null) {
  return useQuery({
    queryKey: [...AGENCY_KEY, "keys", id],
    enabled: !!id,
    queryFn: async (): Promise<ClientApiKey[]> => {
      const r = await api.get(`/agency/clients/${encodeURIComponent(id!)}/api-keys`);
      return (Array.isArray(r.data) ? r.data : []) as ClientApiKey[];
    },
  });
}

export function useCreateClientKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }): Promise<{ id: number; name: string; apiKey: string }> => {
      const r = await api.post(`/agency/clients/${encodeURIComponent(id)}/api-keys`, { name });
      return { id: toNum(r.id), name: String(r.name ?? name), apiKey: String(r.apiKey ?? "") };
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: [...AGENCY_KEY, "keys", v.id] }),
  });
}

export function useRevokeClientKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, keyId }: { id: string; keyId: number }) =>
      api.delete(`/agency/clients/${encodeURIComponent(id)}/api-keys/${keyId}`),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: [...AGENCY_KEY, "keys", v.id] }),
  });
}

export function useClientUsage(id: string | null, days = 30) {
  return useQuery({
    queryKey: [...AGENCY_KEY, "usage", id, days],
    enabled: !!id,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ClientUsage> => {
      const r = await api.get(`/agency/clients/${encodeURIComponent(id!)}/usage?days=${days}`);
      const row = (d: Record<string, unknown>) => ({
        requests: toNum(d.requests),
        charged: toNum(d.charged),
        agencyEarning: toNum(d.agencyEarning),
      });
      return {
        days: toNum(r.days, days),
        totals: row(r.totals ?? {}),
        daily: (Array.isArray(r.daily) ? r.daily : []).map((d: Record<string, unknown>) => ({ date: String(d.date), ...row(d) })),
        byModel: (Array.isArray(r.byModel) ? r.byModel : []).map((m: Record<string, unknown>) => ({
          provider: String(m.provider ?? ""),
          model: String(m.model ?? ""),
          ...row(m),
        })),
      };
    },
  });
}
